import { useCallback, useEffect, useRef, useState } from 'react';
import { CATEGORIES, categoryById, fieldsFor, typeById, typesFor } from '../demo/catalog.js';
import { useDemo } from '../demo/DemoContext.jsx';
import { FACT_CATALOG } from '../demo/rules.js';
import { formatTarget } from '../demo/sla.js';
import { readAdminSettings, saveAdminSettings, saveRules, saveSla, testRules } from '../demo/store.js';
import { wordsForLoadError, wordsForSettingsError } from '../lib/adminSettings.js';
import { Chip, Pri } from '../components/Chips.jsx';
import TicketRules from '../components/TicketRules.jsx';

/* IT administration.
 *
 * The two configurable systems in one place, as in the mockup — targets and the
 * rules that decide which target applies. Both are data, so changing either
 * never needs a deploy.
 */

// The demo tabs keep their order and the first one stays the default: the walkthrough scripts and the
// e2e tests open Admin on "Priority & SLA". "Ticket rules" is the real one and sits last.
const TABS = [
  { id: 'priority', label: 'Priority & SLA' },
  { id: 'types', label: 'Request types' },
  { id: 'categories', label: 'Categories' },
  { id: 'rules', label: 'Ticket rules' },
];

export default function Admin() {
  const [tab, setTab] = useState('priority');
  const { walkthrough } = useDemo();

  return (
    <>
      <section className="hero" style={{ paddingBottom: 0 }}>
        <h1 className="hero__title" style={{ fontSize: 34 }}>
          IT <strong>administration</strong>
        </h1>
        <p className="hero__sub">
          The parts of the help desk that are data rather than code.{' '}
          {walkthrough
            ? <><b>Ticket rules</b> saves for real. The other tabs are a preview and do not save yet.</>
            : <><b>Priority &amp; SLA</b> and <b>Ticket rules</b> save for real. Request types and Categories are view only.</>}
        </p>
      </section>

      <div className="adwin">
        <div className="adtabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`adtab${tab === t.id ? ' adtab--on' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
          {/* In the mockup there were also Knowledge base and People tabs. Both
              are out of scope — the KB waits until resolved tickets have filled
              it, and people come from Entra, not from a screen here. */}
        </div>

        <div className="adin">
          {tab === 'rules' ? <TicketRules /> : null}
          {tab === 'priority' ? <PriorityAndSla /> : null}
          {tab === 'types' ? <RequestTypes /> : null}
          {tab === 'categories' ? <Categories /> : null}
        </div>
      </div>
    </>
  );
}

/* =================================================================== SLA */

/** Minutes read back in the unit a person would say them in. */
function toUnit(mins) {
  if (mins < 60) return { value: mins, unit: 'minutes', factor: 1 };
  if (mins < 600) return { value: mins / 60, unit: mins === 60 ? 'hour' : 'hours', factor: 60 };
  return { value: mins / 600, unit: mins === 600 ? 'day' : 'days', factor: 600 };
}

/* WHERE THE TAB'S LISTS COME FROM (Grant, 2026-10-29).
 *
 * Live app: the server. GET /api/ticket-settings answers `rulesVersions` - the rules and the targets as
 * tickets read them, each with the version a save must quote (api/src/shared/adminRules.js). A save is
 * one whole list, and a STALE answer reloads. `version: null` means saving is not turned on yet
 * (api/sql/021_admin_rules.sql not applied): the lists are shown, and nothing can be saved.
 *
 * Walkthrough build: the demo store in this browser, as before. Nothing there reaches the server.
 */
function useRulesSource() {
  const { reference, walkthrough } = useDemo();
  const [live, setLive] = useState({ status: 'loading' });

  const adopt = useCallback((settings) => {
    const v = settings?.rulesVersions;
    if (!v || !v.rules || !v.sla) {
      setLive({ status: 'error', message: 'The priority rules and targets could not be read right now. Try again in a moment.' });
      return;
    }
    setLive({ status: 'ok', rules: v.rules, sla: v.sla });
  }, []);

  const load = useCallback(async () => {
    try {
      adopt(await readAdminSettings());
    } catch (err) {
      setLive({ status: 'error', message: wordsForLoadError(err) });
    }
  }, [adopt]);

  useEffect(() => {
    if (!walkthrough) load();
  }, [walkthrough, load]);

  if (walkthrough) {
    if (!reference) return { status: 'loading' };
    return {
      status: 'ok',
      demo: true,
      rules: { version: 'demo', list: reference.rules },
      sla: { version: 'demo', list: reference.sla },
      save: async (section, _version, values) => {
        if (section === 'slaTargets') await saveSla(values.sla);
        else await saveRules(values.rules);
        return { changed: true };
      },
      reload: () => {},
    };
  }

  return {
    ...live,
    demo: false,
    // A save answers the fresh settings; adopting them rebuilds the editors from what is stored now.
    save: async (section, version, values) => {
      const out = await saveAdminSettings(section, version, values);
      if (out.settings) adopt(out.settings);
      else load();
      return out;
    },
    reload: load,
  };
}

/* One save of one list, with the words for what happened. Mirrors TicketRules.jsx's section save.
 * `noticeState` is held by PriorityAndSla, above the editors: a save or a STALE reload changes the
 * version, the version-keyed editor remounts, and a notice kept inside it would vanish with it. */
function useListSave(src, section, version, noticeState, draftShape) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = noticeState;
  // A new edit or a Discard makes the last save's words out of date (PR #121 review N2).
  const lastDraft = useRef(draftShape);
  useEffect(() => {
    if (lastDraft.current === draftShape) return;
    lastDraft.current = draftShape;
    setNotice(null);
  }, [draftShape, setNotice]);
  const reload = () => {
    setNotice(null);
    src.reload();
  };
  const run = async (values) => {
    setBusy(true);
    setNotice(null);
    try {
      const out = await src.save(section, version, values);
      setNotice({
        kind: 'saved',
        text: out.changed
          ? (src.demo ? 'Saved in this browser only.' : 'Saved. New tickets use this within 15 minutes.')
          : 'Nothing to change: this is already what is saved.',
      });
    } catch (err) {
      const unknown = !(err && (err.outcome === 'rejected' || err.code === 'WALKTHROUGH_WRITE_BLOCKED'));
      setNotice({ kind: 'error', text: wordsForSettingsError(err), reload: unknown });
      if (err && err.code === 'STALE') src.reload();
    } finally {
      setBusy(false);
    }
  };
  return { run, busy, notice, reload };
}

function SaveNotice({ notice, onReload }) {
  if (!notice) return null;
  if (notice.kind !== 'error') return <p className="why" role="status">{notice.text}</p>;
  return (
    <p className="why" role="alert">
      {notice.text}
      {notice.reload ? <> <button type="button" className="btn btn--quiet" onClick={onReload}>Reload</button></> : null}
    </p>
  );
}

function PriorityAndSla() {
  const src = useRulesSource();
  const slaNotice = useState(null);
  const rulesNotice = useState(null);

  if (src.status === 'loading') return <p className="muted">Loading…</p>;
  if (src.status === 'error') {
    return (
      <div className="banner banner--bad" role="alert">
        <span className="material-symbols-outlined" aria-hidden="true">error</span>
        <span>{src.message}</span>
        <button type="button" className="btn btn--quiet" onClick={src.reload}>Try again</button>
      </div>
    );
  }
  const readOnly = !src.demo && (src.rules.version === null || src.sla.version === null);

  return (
    <>
      {readOnly ? (
        <div className="banner" role="status">
          <span className="material-symbols-outlined" aria-hidden="true">info</span>
          <span>These are the real targets and rules that tickets use. Saving them here is not turned on yet.</span>
        </div>
      ) : null}
      {/* Keyed by version: a save or a reload rebuilds the editor from what is stored. The save notices
          live here, so they outlast that rebuild. */}
      <SlaTargets key={`sla-${src.sla.version}`} src={src} readOnly={readOnly} notice={slaNotice} />
      <Rules key={`rules-${src.rules.version}`} src={src} readOnly={readOnly} notice={rulesNotice} />
    </>
  );
}

function SlaTargets({ src, readOnly, notice }) {
  const saved = src.sla.list;
  const [sla, setSla] = useState(() => saved.map((p) => ({ ...p })));
  const save = useListSave(src, 'slaTargets', src.sla.version, notice, JSON.stringify(sla));
  const slaDirty = JSON.stringify(sla) !== JSON.stringify(saved);

  function update(priority, patch) {
    setSla(sla.map((p) => (p.priority === priority ? { ...p, ...patch } : p)));
  }

  return (
    <>
      <h2 className="adh">Response &amp; resolution targets</h2>
      <p className="adp">
        What each priority promises. The clock pauses whenever a ticket is Waiting, so chasing a
        requester or a supplier never counts against you. Around-the-clock ignores the working
        calendar entirely — everything else runs 07:00–17:00, Mon–Fri.
      </p>

      <div className="sl">
        <div className="sl__head">
          <div>Priority</div>
          <div>When it applies</div>
          <div>First response</div>
          <div>Resolution</div>
          <div>Clock runs</div>
        </div>

        {sla.map((p) => {
          const resp = toUnit(p.responseMins);
          const res = toUnit(p.resolveMins);
          return (
            <div key={p.priority} className="sl__row">
              <div>
                <Pri priority={p.priority} /> <span className="muted">{p.label}</span>
              </div>
              <div className="sl__desc">{p.applies}</div>
              <div className="fld">
                <input
                  type="number"
                  min="1"
                  disabled={readOnly}
                  value={round(resp.value)}
                  aria-label={`${p.priority} first response`}
                  onChange={(e) => update(p.priority, { responseMins: Math.max(1, Number(e.target.value)) * resp.factor })}
                />
                <span>{resp.unit}</span>
              </div>
              <div className="fld">
                <input
                  type="number"
                  min="1"
                  disabled={readOnly}
                  value={round(res.value)}
                  aria-label={`${p.priority} resolution`}
                  onChange={(e) => update(p.priority, { resolveMins: Math.max(1, Number(e.target.value)) * res.factor })}
                />
                <span>{res.unit}</span>
              </div>
              <div>
                <button
                  type="button"
                  className="tog"
                  aria-pressed={p.aroundClock}
                  disabled={readOnly}
                  onClick={() => update(p.priority, { aroundClock: !p.aroundClock })}
                >
                  <span className={`sw${p.aroundClock ? '' : ' sw--off'}`}><i /></span>
                  {p.aroundClock ? '24/7' : 'Work hrs'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="inline" style={{ marginTop: 12 }}>
        {readOnly ? null : (
          <button type="button" className="btn btn--primary" disabled={!slaDirty || save.busy} onClick={() => save.run({ sla })}>
            {save.busy ? 'Saving…' : slaDirty ? 'Save targets' : 'Saved'}
          </button>
        )}
        {slaDirty && !save.busy ? (
          <button type="button" className="btn btn--quiet" onClick={() => setSla(saved.map((p) => ({ ...p })))}>
            Discard
          </button>
        ) : null}
        <span className="muted" style={{ fontSize: 11.5 }}>
          P1 currently promises a reply in {formatTarget(sla[0].responseMins, sla[0].aroundClock)}.
        </span>
      </div>
      <SaveNotice notice={save.notice} onReload={save.reload} />
    </>
  );
}

const round = (n) => (Number.isInteger(n) ? n : Math.round(n * 10) / 10);

/* ============================================================ priority rules */

const OPS = [
  { value: 'eq', label: 'is' },
  { value: 'neq', label: 'is not' },
  { value: 'in', label: 'is one of' },
  { value: 'gte', label: 'is at least' },
  { value: 'lte', label: 'is at most' },
  { value: 'time_between', label: 'is between' },
];

/** Turn a stored condition into the words a person would use for it. */
function describe(condition) {
  const fact = FACT_CATALOG.find((f) => f.fact === condition.fact);
  const op = OPS.find((o) => o.value === condition.op);
  const values = Array.isArray(condition.value) ? condition.value : [condition.value];
  const readable = values
    .map((v) => {
      if (condition.fact === 'category') return categoryById(v)?.label || v;
      if (condition.fact === 'requestType') return typeById(v)?.label || v;
      if (v === true) return 'yes';
      if (v === false) return 'no';
      return v;
    })
    .join(', ');

  return { label: fact?.label || condition.fact, op: op?.label || condition.op, value: readable };
}

/* What a save compares: the parts of a rule that are stored. ("Fired this month" is not.) */
const ruleShape = (rules) => JSON.stringify(
  [...rules].sort((a, b) => a.sortOrder - b.sortOrder)
    .map((r) => [r.ruleId, r.name, r.priority, r.enabled !== false, r.pinned === true, r.conditions, r.because || '']),
);

function Rules({ src, readOnly, notice }) {
  const saved = src.rules.list;
  const [draft, setDraft] = useState(() => saved.map((r) => ({ ...r })));
  const [editing, setEditing] = useState(null);
  const [test, setTest] = useState(null);
  const [testing, setTesting] = useState(false);
  const save = useListSave(src, 'priorityRules', src.rules.version, notice, ruleShape(draft));

  const dirty = ruleShape(draft) !== ruleShape(saved);
  const ordered = [...draft].sort((a, b) => a.sortOrder - b.sortOrder);
  const movable = ordered.filter((r) => !r.pinned);

  function update(ruleId, patch) {
    setDraft(draft.map((r) => (r.ruleId === ruleId ? { ...r, ...patch } : r)));
    setTest(null);
  }

  function move(ruleId, delta) {
    // The pinned default never moves, and nothing can be pushed below it.
    const list = movable.map((r) => ({ ...r }));
    const i = list.findIndex((r) => r.ruleId === ruleId);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    list.forEach((r, n) => {
      r.sortOrder = (n + 1) * 10;
    });
    setDraft([...list, ...ordered.filter((r) => r.pinned).map((r) => ({ ...r }))]);
    setTest(null);
  }

  function addRule() {
    const id = `r-${Date.now()}`;
    const maxSort = Math.max(0, ...movable.map((r) => r.sortOrder));
    setDraft([
      ...draft,
      {
        ruleId: id,
        name: 'New rule',
        priority: 'P2',
        sortOrder: maxSort + 10,
        enabled: true,
        pinned: false,
        conditions: [{ fact: 'category', op: 'eq', value: '' }],
        because: '',
        firedThisMonth: 0,
      },
    ]);
    setEditing(id);
    setTest(null);
  }

  async function runTest() {
    setTesting(true);
    setTest(await testRules(draft));
    setTesting(false);
  }

  const editingRule = draft.find((r) => r.ruleId === editing) || null;

  return (
    <>
      <h2 className="adh" style={{ marginTop: 26 }}>Priority rules</h2>
      <p className="adp">
        Checked top to bottom on every new ticket. <b>First match wins</b> — so the specific
        rules go above the general ones. The ticket records which rule fired, and IT can always
        override.
      </p>

      <div className="rl">
        {ordered.map((rule, i) => {
          const idx = movable.findIndex((r) => r.ruleId === rule.ruleId);
          return (
            <div
              key={rule.ruleId}
              className={`rl__row${editing === rule.ruleId ? ' rl__row--edit' : ''}${rule.enabled === false ? ' rl__row--off' : ''}`}
            >
              <span
                className="material-symbols-outlined grip"
                style={rule.pinned ? { opacity: 0.3 } : undefined}
                aria-hidden="true"
              >
                drag_indicator
              </span>
              <span className="ord">{rule.pinned ? '—' : i + 1}</span>

              <button
                type="button"
                className="rl__body"
                style={bodyBtn}
                onClick={() => setEditing(rule.pinned ? null : editing === rule.ruleId ? null : rule.ruleId)}
                disabled={rule.pinned || readOnly}
              >
                <b style={rule.pinned ? { color: 'var(--ihs-ink-3)' } : undefined}>{rule.name}</b>
                <span className="cond">
                  {rule.conditions.length === 0 ? (
                    <span className="cond__c">Always applies, last</span>
                  ) : (
                    rule.conditions.map((c, n) => {
                      const d = describe(c);
                      return (
                        <span key={n} style={{ display: 'contents' }}>
                          {n > 0 ? <span className="cond__and">and</span> : null}
                          <span className="cond__c">
                            <b>{d.label}</b> {d.op} {d.value}
                          </span>
                        </span>
                      );
                    })
                  )}
                </span>
              </button>

              <Pri priority={rule.priority} />

              {!rule.pinned && !readOnly ? (
                <span className="rl__acts">
                  <button type="button" className="btn btn--ghost" aria-label="Move up" disabled={idx === 0} onClick={() => move(rule.ruleId, -1)}>
                    <span className="material-symbols-outlined" aria-hidden="true">arrow_upward</span>
                  </button>
                  <button type="button" className="btn btn--ghost" aria-label="Move down" disabled={idx === movable.length - 1} onClick={() => move(rule.ruleId, 1)}>
                    <span className="material-symbols-outlined" aria-hidden="true">arrow_downward</span>
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    aria-label={rule.enabled === false ? 'Turn on' : 'Turn off'}
                    onClick={() => update(rule.ruleId, { enabled: rule.enabled === false })}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      {rule.enabled === false ? 'toggle_off' : 'toggle_on'}
                    </span>
                  </button>
                </span>
              ) : null}

              {/* The live app does not count rule hits yet, so it shows no count rather than a made-up one. */}
              {src.demo ? (
                <span className="rl__hits">
                  <b>{rule.firedThisMonth ?? 0}</b>
                  fired this month
                </span>
              ) : null}
            </div>
          );
        })}
      </div>

      {editingRule ? (
        <Builder
          rule={editingRule}
          priorities={src.sla.list.map((p) => p.priority)}
          onChange={(patch) => update(editingRule.ruleId, patch)}
          onClose={() => setEditing(null)}
          onDelete={() => {
            setDraft(draft.filter((r) => r.ruleId !== editingRule.ruleId));
            setEditing(null);
            setTest(null);
          }}
        />
      ) : null}

      {readOnly ? null : (
      <div className="inline" style={{ marginTop: 13 }}>
        <button type="button" className="btn btn--quiet" onClick={addRule}>
          <span className="material-symbols-outlined" aria-hidden="true">add</span>
          Add a rule
        </button>
        {/* The test replays the demo tickets in this browser, so only the walkthrough build offers it. */}
        {src.demo ? (
          <button type="button" className="btn btn--quiet" onClick={runTest} disabled={testing}>
            <span className="material-symbols-outlined" aria-hidden="true">science</span>
            {testing ? 'Testing…' : 'Test against tickets already raised'}
          </button>
        ) : null}
        <button type="button" className="btn btn--primary" disabled={!dirty || save.busy} onClick={() => save.run({ rules: draft })}>
          {save.busy ? 'Saving…' : dirty ? 'Save rules' : 'Saved'}
        </button>
        {dirty && !save.busy ? (
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              setDraft(saved.map((r) => ({ ...r })));
              setTest(null);
              setEditing(null);
            }}
          >
            Discard changes
          </button>
        ) : null}
      </div>
      )}
      <SaveNotice notice={save.notice} onReload={save.reload} />

      {test ? <TestResults rows={test} /> : null}

      <div className="warnbox">
        <span className="material-symbols-outlined" aria-hidden="true">history</span>
        <div>
          <b>Every override is recorded.</b> When IT changes a priority the rules set, we store
          the ticket, the rule, the old and new priority and the reason given. That is the
          training set for handing this to AI later — and in the meantime it is how you find
          rules that are quietly wrong, like one that fires every Saturday and gets downgraded
          every Monday.
        </div>
      </div>
    </>
  );
}

const bodyBtn = {
  border: 'none',
  background: 'none',
  padding: 0,
  textAlign: 'left',
  cursor: 'pointer',
  font: 'inherit',
  color: 'inherit',
};

function Builder({ rule, priorities, onChange, onClose, onDelete }) {
  const { walkthrough } = useDemo();
  const families = [...new Set(FACT_CATALOG.map((f) => f.family))];
  const conditions = rule.conditions || [];

  const setCondition = (i, patch) =>
    onChange({ conditions: conditions.map((c, n) => (n === i ? { ...c, ...patch } : c)) });

  return (
    <div className="bld">
      <div className="bld__head">
        <span className="material-symbols-outlined" aria-hidden="true">tune</span>
        Editing — {rule.name}
      </div>

      <div className="field">
        <span className="field__label">Rule name</span>
        <input className="input" value={rule.name} onChange={(e) => onChange({ name: e.target.value })} />
      </div>

      <span className="field__label">When all of these are true</span>
      {conditions.map((c, i) => (
        <div key={i} className="line">
          <select
            className="select select--auto"
            value={c.fact}
            aria-label="Fact"
            onChange={(e) => setCondition(i, { fact: e.target.value })}
          >
            {families.map((fam) => (
              <optgroup key={fam} label={fam}>
                {FACT_CATALOG.filter((f) => f.family === fam).map((f) => (
                  <option key={f.fact} value={f.fact}>{f.label}</option>
                ))}
              </optgroup>
            ))}
          </select>

          <select
            className="select select--auto"
            value={c.op}
            aria-label="Operator"
            onChange={(e) => setCondition(i, { op: e.target.value })}
          >
            {OPS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <input
            className="input"
            style={{ width: 'auto', flex: 1, minWidth: 130 }}
            aria-label="Value"
            placeholder={c.op === 'in' || c.op === 'time_between' ? 'comma separated' : ''}
            value={Array.isArray(c.value) ? c.value.join(', ') : String(c.value ?? '')}
            onChange={(e) => setCondition(i, { value: parseValue(e.target.value, c.op) })}
          />

          <button
            type="button"
            className="btn btn--ghost"
            aria-label="Remove condition"
            onClick={() => onChange({ conditions: conditions.filter((_, n) => n !== i) })}
          >
            <span className="material-symbols-outlined" aria-hidden="true">close</span>
          </button>
        </div>
      ))}

      <div className="line">
        <button
          type="button"
          className="addc"
          onClick={() => onChange({ conditions: [...conditions, { fact: 'category', op: 'eq', value: '' }] })}
        >
          + Add condition
        </button>
      </div>

      <div className="then">
        <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: 16 }}>arrow_forward</span>
        Then set priority to
        <select
          className="select select--auto"
          value={rule.priority}
          aria-label="Resulting priority"
          onChange={(e) => onChange({ priority: e.target.value })}
        >
          {/* The priorities that have targets: the server refuses any other. */}
          {priorities.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <Pri priority={rule.priority} />
      </div>

      {/* The replay and "Test against tickets already raised" exist only in the walkthrough build. */}
      {walkthrough ? (
        <div className="prev">
          <b>In the real build this box replays the rule over the last 90 days</b> and reports how
          many tickets it would have caught and how often IT overrode the result — "fired on 112,
          overridden on 9, mostly down to P3 on Saturdays". Here, use{' '}
          <b>Test against tickets already raised</b> below for the same idea at demo scale.
        </div>
      ) : null}

      <div className="inline" style={{ marginTop: 13 }}>
        <button type="button" className="btn btn--primary" onClick={onClose}>Done</button>
        <button type="button" className="btn btn--quiet" onClick={onDelete}>Delete this rule</button>
      </div>
    </div>
  );
}

/** Multi-value ops keep an array; numbers stay numbers; `true`/`false` stay booleans. */
function parseValue(raw, op) {
  if (op === 'in' || op === 'time_between') return raw.split(',').map((s) => s.trim()).filter(Boolean);
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  if (raw !== '' && !Number.isNaN(Number(raw))) return Number(raw);
  return raw;
}

function TestResults({ rows }) {
  const changed = rows.filter((r) => r.changed);
  return (
    <div className="card card--pad" style={{ marginTop: 14 }}>
      <div className="section__title" style={{ marginBottom: 6 }}>
        Replayed over {rows.length} {rows.length === 1 ? 'ticket' : 'tickets'}
      </div>
      <p className="adp" style={{ marginBottom: 10 }}>
        {changed.length === 0
          ? 'Nothing would land differently. Safe to save.'
          : `${changed.length} would get a different priority than they have now.`}
      </p>
      {changed.length ? (
        <div className="tablewrap">
          <table className="table">
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Now</th>
                <th>Would be</th>
                <th>By rule</th>
              </tr>
            </thead>
            <tbody>
              {changed.map((r) => (
                <tr key={r.number}>
                  <td>
                    <span className="ref">{r.number}</span> {r.title}{' '}
                    {r.overridden ? <Chip tone="warn">IT overrode this one</Chip> : null}
                  </td>
                  <td><Pri priority={r.was} /></td>
                  <td><Pri priority={r.wouldBe} /></td>
                  <td className="muted">{r.rule}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

/* =========================================================== the catalogue */

/* On the live app these two tabs are view only: nothing here writes, and the list comes from the
 * app's built-in catalogue (demo/catalog.js), so a change needs an app update (BACKLOG: "The other Admin tabs"). */
function ViewOnlyNote() {
  return (
    <p className="adp" role="note">
      <b>View only.</b> This list cannot be changed here. A change needs an update to the app; ask the app owner.
    </p>
  );
}

function RequestTypes() {
  const [open, setOpen] = useState(CATEGORIES[0].id);
  const { walkthrough } = useDemo();

  return (
    <>
      <h2 className="adh">Request types and their questions</h2>
      {walkthrough ? (
      <p className="adp">
        <b>This is the list to argue with.</b> The questions here are the triage — they are what
        the priority rules and the queue filters read, so a missing question is a ticket IT has
        to chase. In the real build this is editable data: adding a request type is data entry,
        not a deploy. Read-only in the prototype on purpose — say what is wrong and it changes
        before the tables exist.
      </p>
      ) : (
        <>
          <p className="adp">
            Each request type and the questions its form asks. The answers feed the priority rules and
            the queue filters.
          </p>
          <ViewOnlyNote />
        </>
      )}

      <div className="stack">
        {CATEGORIES.map((c) => {
          const types = typesFor(c.id);
          const isOpenCat = open === c.id;
          return (
            <div key={c.id} className="card">
              <button type="button" className="inline" style={{ ...bodyBtn, width: '100%' }} onClick={() => setOpen(isOpenCat ? null : c.id)}>
                <span className="tk__icon">
                  <span className="material-symbols-outlined" aria-hidden="true">{c.icon}</span>
                </span>
                <span style={{ font: '500 14px Roboto, sans-serif' }}>{c.label}</span>
                <Chip tone="flat">{types.length} {types.length === 1 ? 'type' : 'types'}</Chip>
                <Chip tone="app">{c.share}</Chip>
                <span className="material-symbols-outlined li__go" style={{ marginLeft: 'auto' }} aria-hidden="true">
                  {isOpenCat ? 'expand_less' : 'expand_more'}
                </span>
              </button>

              {isOpenCat ? (
                <div style={{ marginTop: 12 }}>
                  {types.map((t) => (
                    <div key={t.id} style={{ padding: '12px 0', borderTop: '1px solid var(--ihs-divider)' }}>
                      <div className="inline">
                        <span className="material-symbols-outlined" style={{ fontSize: 17, color: 'var(--ihs-mid-blue)' }} aria-hidden="true">
                          {t.icon}
                        </span>
                        <span style={{ font: '500 13px Roboto, sans-serif' }}>{t.label}</span>
                        <span className="muted" style={{ fontSize: 11 }}>{t.blurb}</span>
                      </div>
                      <ul style={{ margin: '8px 0 0', paddingLeft: 18, color: 'var(--ihs-ink-3)', font: '12px/1.7 Roboto, sans-serif' }}>
                        {fieldsFor(t.id).map((f) => (
                          <li key={f.key}>
                            {f.label}
                            {f.type === 'choice' ? (
                              <span className="muted"> — {f.options.map((o) => o.label).join(' / ')}</span>
                            ) : (
                              <span className="muted"> — {f.type === 'textarea' ? 'free text' : 'short answer'}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </>
  );
}

function Categories() {
  const { walkthrough } = useDemo();
  return (
    <>
      <h2 className="adh">Categories</h2>
      {walkthrough ? (
      <p className="adp">
        Sized from the 100 most recent tickets on the Asana board. Two things worth arguing
        about: the existing board has no Phones, ServiceTitan or Network category at all — its
        closest phone option is a system IHS has since left, which is most of why "Other"
        dominates. And ten is at the very top of what a person will scan; Microsoft 365 folds
        into Accounts &amp; access and App-on-my-phone into Devices if eight is wanted.
      </p>
      ) : (
        <>
          <p className="adp">
            The categories a person picks from when they raise a ticket. Share is an estimate from the
            100 most recent tickets on the old Asana board (August 2026). New categories have no share yet.
          </p>
          <ViewOnlyNote />
        </>
      )}

      <div className="sl">
        <div className="sl__head" style={{ gridTemplateColumns: '52px minmax(0,1fr) 150px 120px 120px' }}>
          <div />
          <div>Category</div>
          <div>Covers</div>
          <div>Share</div>
          <div>Request types</div>
        </div>
        {CATEGORIES.map((c) => (
          <div key={c.id} className="sl__row" style={{ gridTemplateColumns: '52px minmax(0,1fr) 150px 120px 120px' }}>
            <span className="tk__icon">
              <span className="material-symbols-outlined" aria-hidden="true">{c.icon}</span>
            </span>
            <div style={{ font: '500 12.5px Roboto, sans-serif' }}>{c.label}</div>
            <div className="sl__desc">{c.blurb}</div>
            <div className="muted">{c.share}</div>
            <div className="muted">{typesFor(c.id).length}</div>
          </div>
        ))}
      </div>
    </>
  );
}
